import React from "react"
import PropTypes from "prop-types"
import styled, { css } from "styled-components"
import tw from "twin.macro"
import { PrismicRichText } from "@prismicio/react"
import { linkResolver } from "utils/linkResolver"
import { htmlSerializer } from "components/HeroSection"
import { Link } from "gatsby"
import { Icon } from "@clevertrack/shared"
import { IconSizeEnum } from "lib/Icon"

const StyledHeadline = styled.h1<{ $preserveH1Style?: boolean }>`
  // margin-top: 0;

  span {
    ${tw`text-brand-500`}
  }
  ${(props) => props.theme.media.tablet_landscape_up`
    line-height: 1.3;
  `}

  ${(props) =>
    props.$preserveH1Style &&
    css`
      margin: 0 0 0.33em;
      font-family: ${props.theme.fontStacks.heading};
      font-size: 2.8rem;
      line-height: 1.2;

      ${props.theme.media.tablet_landscape_up`
        font-size: 3.2rem;
        line-height: 1.3;
      `}
    `}
`

const StyledHyperlink = styled.a<{ isExternal?: boolean }>`
  ${tw`text-brand-500`}
  p & {
    ${(props) => (props.isExternal ? tw`inline` : tw`inline-flex items-center`)}

    :has(> &:only-child) {
      svg {
        ${tw`hidden`}
      }
    }
  }

  svg {
    ${tw`ml-2 mr-2 w-6 h-6`}
  }
`

const StyledQuote = styled.blockquote`
  max-width: 75rem;
  margin-left: auto;
  font-family: ${(props) => props.theme.fontStacks.light};
  font-size: 1.8rem;
  line-height: 1.6;
  position: relative;
  ${tw`italic mt-8 mb-12 block md:(mt-36 mb-16)`}

  .cite {
    position: absolute;
    font-size: 51.2rem;
    top: 65%;
    text-align: left;
    right: 5%;
    left: auto;
    line-height: 0;
    opacity: 0.08;
  }

  ${(props) => props.theme.media.tablet_landscape_up`
    font-size: 2.4rem;
  `}
`

const StyledEmbed = styled.div`
  width: 100%;
  margin: 2rem 0;

  iframe {
    width: 100%;
    min-height: 22rem;
  }

  ${(props) => props.theme.media.tablet_landscape_up`
    width: 100%;
    margin: 2rem auto;

    iframe {
      min-height: 56.2rem;
    }
  `}
`

const StyledBullet = styled.li`
  // margin: 0 0 0.5rem;
  padding: 0.5rem 0;
  line-height: 1.6;
  position: relative;
  list-style: none;
  display: grid;
  align-items: start;
  grid-column-gap: 1rem;
  grid-template-columns: 2.2rem 1fr;

  a {
    display: block;
    align-items: center;
  }

  span {
    width: 2.2rem;
    height: 2.2rem;
    margin-top: 0.3rem;
    border-radius: 50%;
    position: relative;
    display: inline-block;
    color: #fff;
    ${tw`bg-brand-500`}
    svg {
      left: 50%;
      top: 50%;
      transform: translate3d(-50%, -50%, 0);
      position: absolute;
      z-index: 20;
      fill: currentColor;
      width: 1.2rem;
      height: 1.2rem;
    }
  }

  ${(props) => props.theme.media.tablet_landscape_up`

  `}
`

type ContentProps = {
  content?: any
  serializer?: any
  color?: any
  bgColor?: any
  icon?: any
  isQuote?: boolean
  inline?: boolean
  h2ash1?: boolean
  h1ash2?: boolean
  h1ash3?: boolean
  noInternalLinkArrows?: boolean
  [key: string]: any
}

function Content({
  content,
  serializer,
  color,
  bgColor,
  icon,
  isQuote,
  inline,
  h2ash1,
  h1ash2,
  h1ash3,
  noInternalLinkArrows,
  ...props
}: ContentProps) {
  if (!content) {
    return null
  }
  return (
    <PrismicRichText
      field={content.richText}
      linkResolver={linkResolver}
      components={{
        heading1: ({ children, node, text, key }) => {
          return (
            <StyledHeadline
              key={key || "h1"}
              as={h1ash3 ? "h3" : h1ash2 ? "h2" : "h1"}
              $preserveH1Style={h1ash2}
              dangerouslySetInnerHTML={{
                __html: node.text
                  .replace("*", "<span>")
                  .replace("*", "</span>"),
              }}
            />
          )
        },
        heading2: ({ children, node, text, key }) => {
          return (
            <StyledHeadline
              key={key || "h2"}
              as={h2ash1 ? "h1" : "h2"}
              dangerouslySetInnerHTML={{
                __html: node.text
                  .replace("*", "<span>")
                  .replace("*", "</span>"),
              }}
            />
          )
        },
        heading3: ({ children, node, text, key }) => {
          return (
            <StyledHeadline
              key={key || "h3"}
              as="h3"
              dangerouslySetInnerHTML={{
                __html: node.text
                  .replace("*", "<span>")
                  .replace("*", "</span>"),
              }}
            />
          )
        },
        hyperlink: ({ node, children }) => {
          const url = linkResolver(node.data)
          if (["page", "blog_post"].includes(node.data?.type)) {
            return (
              <StyledHyperlink
                as={Link}
                to={url}
                isExternal={["Media", "Web"].includes(node.data.link_type)}
              >
                {children}
                {node.end === node.text.length && (
                  <Icon icon="long-arrow-right" size={IconSizeEnum.AUTO} />
                )}
              </StyledHyperlink>
            )
          }
          return (
            <StyledHyperlink
              href={url}
              rel="noopener"
              target={node.data?.target}
              isExternal={["Media", "Web"].includes(node.data?.link_type)}
            >
              {children}
              {node.data?.target === "_blank" && (
                <Icon icon="external-link" size={IconSizeEnum.AUTO} />
              )}
            </StyledHyperlink>
          )
        },
        list: ({ children, node }) => {
          return (
            <ul tw="px-0 space-y-4">
              {node.items.map((item, index) => {
                return (
                  <StyledBullet key={index}>
                    <span>
                      <Icon icon="check" size={IconSizeEnum.MD} />
                    </span>
                    {item.text}
                  </StyledBullet>
                )
              })}
            </ul>
          )
        },

        embed: ({ node }) => (
          <StyledEmbed
            dangerouslySetInnerHTML={{ __html: node.oembed.html ?? "" }}
          />
        ),

        paragraph: ({ children }) =>
          isQuote ? (
            <StyledQuote>
              <span className="cite">&#8221;</span>
              <span className="inner">{children}</span>
            </StyledQuote>
          ) : inline ? (
            <span>{children}</span>
          ) : (
            <p>{children}</p>
          ),
      }}
      {...props}
    />
  )
}

export default Content

Content.propTypes = {
  content: PropTypes.array.isRequired,
}
